<template>
  <fieldset :disabled="disabled" class="dynamic-section">
    <b-alert v-if="individualHint" show variant="info" class="p-75">
      <small>{{ individualHint }}</small>
    </b-alert>

    <b-row>
      <template v-for="item in section.items">
        <!-- read and sign -->
        <b-col v-if="item.kind === 'Notice'" :key="item.key" cols="12">
          <notice-block
            :ref="'notice_' + item.key"
            :item="item"
            :value="ensureObject(item.dataKey, { acknowledged: false, name: '' })"
            :disabled="disabled"
            :show-hint="showHints"
            :active="active"
            :hide-title="section.items.length === 1"
            :existing="(noticeSignatures && noticeSignatures[item.key]) || null"
          />
        </b-col>

        <!-- re-categorisation choice, criteria and the Retail rule -->
        <b-col v-else-if="item.builtinKey === 'recategorisation'" :key="item.key" cols="12">
          <section-recategorisation :section="ensureObject(item.dataKey, recatDefault())" :disabled="disabled" />
          <small v-if="item.help" class="text-muted d-block">{{ item.help }}</small>
        </b-col>

        <!-- headings span the row -->
        <b-col v-else-if="item.inputType === 'heading'" :key="item.key" cols="12">
          <question-field :item="item" :form="form" />
        </b-col>

        <!-- everything else -->
        <b-col v-else :key="item.key" cols="12" :md="item.cols || 12">
          <question-field
            :item="item"
            :value="read(item.dataKey)"
            :form="form"
            :disabled="disabled || isLocked(item)"
            :locked-note="isLocked(item) ? 'Set by SFC for this account' : null"
            @input="write(item.dataKey, $event)"
          />
        </b-col>
      </template>
    </b-row>
  </fieldset>
</template>

<script>
// Renders one non-person section of the form schema straight into the form object.
import QuestionField from "./QuestionField.vue";
import NoticeBlock from "./NoticeBlock.vue";
import SectionRecategorisation from "./sections/SectionRecategorisation.vue";
import { getPath, defaultValue } from "./formSchema";

export default {
  name: "DynamicSection",

  components: { QuestionField, NoticeBlock, SectionRecategorisation },

  props: {
    section: { type: Object, required: true },
    form: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    showHints: { type: Boolean, default: false },
    active: { type: Boolean, default: true },
    preview: { type: Boolean, default: false },
    noticeSignatures: { type: [Object, Array], default: () => ({}) },
    // data keys fixed by the account (e.g. the client type), shown but not editable
    lockedKeys: { type: Array, default: () => [] },
  },

  computed: {
    // the Knowledge and Experience questions are optional for individuals
    individualHint() {
      const conditional = this.section.items.filter(
        (item) => item.condition && item.condition.then === "optional" && item.required
      );
      if (!conditional.length) return null;
      const applies = conditional.some((item) => {
        const value = getPath(this.form, item.condition.when);
        return (item.condition.in || []).indexOf(value === null || value === undefined ? null : String(value)) !== -1;
      });
      if (!applies) return null;
      const value = getPath(this.form, conditional[0].condition.when);
      return `Some questions in this section are optional for ${String(value).toLowerCase()} clients.`;
    },
  },

  methods: {
    isLocked(item) {
      return this.lockedKeys.indexOf(item.dataKey) !== -1;
    },

    read(path) {
      return getPath(this.form, path);
    },

    // writes through $set so new keys (custom questions) become reactive
    write(path, value) {
      const parts = String(path).split(".");
      let current = this.form;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]] || typeof current[parts[i]] !== "object") {
          this.$set(current, parts[i], {});
        }
        current = current[parts[i]];
      }
      this.$set(current, parts[parts.length - 1], value);
    },

    ensureObject(path, fallback) {
      const existing = getPath(this.form, path);
      if (existing && typeof existing === "object") return existing;
      this.write(path, fallback);
      return getPath(this.form, path);
    },

    recatDefault() {
      return defaultValue({ kind: "Question", builtinKey: "recategorisation" });
    },

    // {noticeKey: dataUrl} for every Signature notice drawn in this section
    noticeSignaturesDrawn() {
      const out = {};
      this.section.items.forEach((item) => {
        if (item.kind !== "Notice" || !item.notice || item.notice.ackMode !== "Signature") return;
        const ref = this.$refs["notice_" + item.key];
        const block = Array.isArray(ref) ? ref[0] : ref;
        const image = block ? block.signature() : null;
        if (image) out[item.key] = image;
      });
      return out;
    },
  },
};
</script>

